import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  getListSecretsQueryKey,
  useDeleteSecret2,
} from '@/generated/hooks/gringotts/v1/services/secret_service';
import { captureException } from '@/modules/sentry';

import {
  clearPendingOAuthConnection,
  readPendingOAuthConnection,
} from '../oauth-resume';

/**
 * Deletes the new credential of a sign-in the user backed out of (see
 * `dropAbandonedManagedOAuthMarker`), so it doesn't hold its name or linger
 * unauthorized. Mount once, app-wide.
 */
export function useDiscardAbandonedOAuthSignIn() {
  const queryClient = useQueryClient();
  const { mutateAsync: deleteSecret } = useDeleteSecret2();

  // eslint-disable-next-line no-restricted-syntax -- reason: reacts to a page load or a page restored by Back, which no app code drives
  useEffect(() => {
    const discard = () => {
      const pending = readPendingOAuthConnection();
      if (!pending?.abandoned) return;
      clearPendingOAuthConnection();
      const { organizationId, workspaceId } = pending;
      void deleteSecret({
        organizationId,
        workspaceId,
        id: pending.credentialId,
      })
        .catch(error =>
          captureException(error, {
            tags: { module: 'credentials', action: 'discardAbandonedSignIn' },
          }),
        )
        .finally(() =>
          queryClient.invalidateQueries({
            queryKey: getListSecretsQueryKey(organizationId),
          }),
        );
    };
    discard();
    // eslint-disable-next-line no-restricted-syntax -- reason: Back restores the page from the cache without remounting
    window.addEventListener('pageshow', discard);
    return () => window.removeEventListener('pageshow', discard);
  }, [deleteSecret, queryClient]);
}
